import React, { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';

import { addToast } from '~actions';

const Wrapper = styled.div<{
  bg?: string;
  color?: string;
  width?: string;
  large?: boolean;
  hover: boolean;
  wrapText?: boolean;
  disabled?: boolean;
  marginTop?: number;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: ${(props) => (props.large ? '64px' : '48px')};
  border-radius: 4px;
  padding: 12px;
  position: relative;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.24px;
  line-height: 24px;
  text-transform: uppercase;
  box-sizing: border-box;
  width: 100%;
  text-align: center;
  margin-top: ${({ marginTop }) => marginTop}px;
  background: ${(props) => (props.bg ? props.bg : '#fbfbfb')};
  color: ${(props) => (props.color ? props.color : '#1c232c')};
  width: ${(props) => (props.width ? props.width : '100%')};
  white-space: ${(props) => (props.wrapText ? 'normal' : 'nowrap')};
  opacity: ${({ disabled }) => (disabled ? 0.7 : 1)};

  ${({ hover, disabled }) =>
    hover &&
    !disabled &&
    `
  transform: scale(0.97);
  `}

  & + & {
    margin-top: 8px;
  }
`;

const Icon = styled.div<{
  icon: string | false;
}>`
  height: 24px;
  width: 24px;
  background-color: #8b8f93;
  mask-image: ${(props) => (props.icon ? 'url(' + props.icon + ')' : 'none')};
  mask-size: contain;
  mask-repeat: no-repeat;
  mask-position: center;
  margin-left: 6px;
`;

interface Props {
  bg?: string;
  color?: string;
  width?: string;
  icon?: string | false;
  large?: boolean;
  children: string | React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onLongPress?: () => void;
  onLongPressEnd?: () => void;
  onTouchStart?: (e: React.TouchEvent<any>) => void;
  onTouchEnd?: (e: React.TouchEvent<any>) => void;
  disabled?: boolean;
  disabledToast?: string;
  wrapText?: boolean;
  marginTop?: number;
}

const Button: React.FunctionComponent<Props> = ({
  bg,
  color,
  width,
  icon,
  large,
  onClick,
  children,
  onLongPress,
  onTouchStart,
  onTouchEnd,
  disabled,
  disabledToast,
  wrapText,
  marginTop = 0,
  ...rest
}) => {
  const [hover, setHover] = useState(false);
  const [touching, setTouching] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const dispatch = useDispatch();

  const handleLongPress = () => {
    setTouching(true);

    timeoutRef.current = setTimeout(() => {
      onLongPress && onLongPress();
      setTouching(false);
    }, 500);
  };

  const handleLongPressEnd = () => {
    if (touching) {
      clearTimeout(timeoutRef.current!);
      setTouching(false);
    }
  };

  return (
    <Wrapper
      bg={bg}
      color={color}
      width={width}
      large={large}
      hover={hover}
      wrapText={wrapText}
      disabled={disabled}
      marginTop={marginTop}
      onTouchStart={(e) => {
        setHover(true);

        if (onTouchStart) {
          if (disabled) {
            if (disabledToast) {
              dispatch(addToast(disabledToast, true));
            }
            return;
          }
          onTouchStart(e);
        }

        if (onLongPress) {
          handleLongPress();
        }
      }}
      onTouchEnd={(e) => {
        setHover(false);

        if (onTouchEnd) {
          if (disabled) {
            if (disabledToast) {
              dispatch(addToast(disabledToast, true));
            }
            return;
          }
          onTouchEnd(e);
        }

        if (onLongPress) {
          handleLongPressEnd();
        }
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled && onClick) {
          onClick(e);
        }
      }}
      {...rest}>
      {children}
      {icon && <Icon icon={icon} />}
    </Wrapper>
  );
};

export default React.memo(Button);